import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { blogService } from "../../../api/services/blogService";

const BlogDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const loadArticle = useCallback(async () => {
    if (!slug) return;
    try {
      setLoading(true);
      const response = await blogService.getBlogBySlug(slug);
      setArticle(response.data);
    } catch (error) {
      console.error("Failed to load article:", error);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadArticle();
  }, [loadArticle]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!article) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h4">Article not found</Typography>
      </Container>
    );
  }

  const tags = article.tags
    ? article.tags.split(",").map((tag: string) => tag.trim())
    : [];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {article.category && (
        <Box sx={{ mb: 2 }}>
          <Chip label={article.category} color="primary" size="small" />
        </Box>
      )}

      <Typography variant="h3" sx={{ mb: 2, fontWeight: 700 }}>
        {article.title}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {article.author_name && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar sx={{ width: 32, height: 32 }}>
              {article.author_name.charAt(0).toUpperCase()}
            </Avatar>
            <Typography variant="body2" fontWeight={600}>
              {article.author_name}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, color: "text.secondary" }}>
          <Typography variant="caption">
            {new Date(article.published_at).toLocaleDateString()}
          </Typography>
          <Typography variant="caption">•</Typography>
          <Typography variant="caption">{article.view_count} views</Typography>
        </Box>
      </Box>

      {article.featured_image && (
        <Box sx={{ mb: 3 }}>
          <img
            src={article.featured_image}
            alt={article.title}
            style={{
              width: "100%",
              height: "auto",
              borderRadius: "8px",
              maxHeight: "400px",
              objectFit: "cover",
            }}
          />
        </Box>
      )}

      {article.excerpt && (
        <Typography
          variant="subtitle1"
          color="text.secondary"
          sx={{ mb: 3, fontStyle: "italic" }}
        >
          {article.excerpt}
        </Typography>
      )}

      <Box
        sx={{
          "& h1": { fontSize: "1.75rem", fontWeight: 600, mt: 3, mb: 1.5 },
          "& h2": { fontSize: "1.5rem", fontWeight: 600, mt: 2.5, mb: 1.5 },
          "& h3": { fontSize: "1.25rem", fontWeight: 600, mt: 2, mb: 1 },
          "& p": { mb: 1.5, lineHeight: 1.7 },
          "& ul, & ol": { mb: 1.5, pl: 2.5 },
          "& li": { mb: 0.5 },
          "& img": {
            maxWidth: "100%",
            height: "auto",
            borderRadius: "4px",
            my: 2,
          },
          "& pre": {
            p: 1.5,
            bgcolor: "grey.100",
            borderRadius: 1,
            overflow: "auto",
            my: 1.5,
          },
          "& code": {
            bgcolor: "grey.100",
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            fontSize: "0.9em",
          },
          "& blockquote": {
            borderLeft: "3px solid",
            borderColor: "primary.main",
            pl: 1.5,
            py: 0.5,
            my: 1.5,
            fontStyle: "italic",
            color: "text.secondary",
          },
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw, rehypeSanitize]}
        >
          {article.content}
        </ReactMarkdown>
      </Box>

      {tags.length > 0 && (
        <Box
          sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider" }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Tags
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {tags.map((tag: string, index: number) => (
              <Chip key={index} label={tag} size="small" variant="outlined" />
            ))}
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default BlogDetail;
